import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import PersonSearchRounded from '@mui/icons-material/PersonSearchRounded';
import SportsGymnasticsRounded from '@mui/icons-material/SportsGymnasticsRounded';
import VerifiedRounded from '@mui/icons-material/VerifiedRounded';
import { Link as RouterLink } from 'react-router';
import { GuidesSection } from '@/guides/GuidesSection';
import { useTenant } from '@/tenant/useTenant';
import { transition } from '@/theme';
import { useCitySuggestions, useProfessionalSearch } from '../api';
import { Faq } from '../components/Faq';
import { ForTrainersSection } from '../components/ForTrainersSection';
import { ProfileCardView } from '../components/ProfileCardView';
import { SearchPanel } from '../components/SearchPanel';
import { cityPath, specialtyPath } from '../format';
import { writeSearch } from '../searchUrl';

const QUICK_SEARCHES = [
  { label: 'Online', to: '/personal-online' },
  { label: 'Aula experimental grátis', to: writeSearch({ trial: true }) },
  { label: 'Personal mulher', to: writeSearch({ gender: 'feminino' }) },
  { label: 'Emagrecimento', to: specialtyPath('emagrecimento') },
  { label: 'Hipertrofia', to: specialtyPath('hipertrofia') },
  { label: 'Terceira idade', to: specialtyPath('terceira-idade') },
  { label: 'Gestantes', to: specialtyPath('gestantes') },
];

export function MarketplaceHomePage() {
  const { displayName } = useTenant();
  useEffect(() => {
    document.title = `Encontre personal trainer perto de você ou online | ${displayName}`;
  }, [displayName]);

  return (
    <>
      <Hero />
      <TrustStrip />
      <HowItWorks />
      <Featured />
      <PopularCities />
      <GuidesSection title="Guias para escolher o seu personal" />
      <ForTrainersSection />
      <Faq />
    </>
  );
}

function Hero() {
  const [who, setWho] = useState<'student' | 'trainer'>('student');
  const featured = useProfessionalSearch({ verified: true, pageSize: 3 });
  const cards = featured.data?.items ?? [];

  return (
    <Box
      component="section"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 5, md: 9 },
        pb: { xs: 7, md: 11 },
        background: [
          `radial-gradient(60vmax 40vmax at 0% 0%, ${theme.alpha(theme.vars.palette.accent.main, 0.22)}, transparent 60%)`,
          `radial-gradient(60vmax 50vmax at 100% 20%, ${theme.alpha(theme.vars.palette.primary.light, 0.24)}, transparent 60%)`,
          theme.vars.palette.background.default,
        ].join(', '),
      })}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 6 }} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={3}>
              <Chip
                icon={<VerifiedRounded />}
                variant="soft"
                color="success"
                label="Personais com CREF conferido pela equipe"
                sx={{ alignSelf: 'flex-start' }}
              />
              <Typography
                variant="h1"
                sx={{ fontSize: { xs: '2.35rem', sm: '3rem', md: '3.5rem' }, maxWidth: 640 }}
              >
                {who === 'student' ? (
                  <>
                    Encontre o personal certo para o{' '}
                    <Box
                      component="span"
                      sx={(theme) => ({
                        background: `linear-gradient(100deg, ${theme.vars.palette.primary.main}, ${theme.vars.palette.accent.dark})`,
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: 'transparent',
                      })}
                    >
                      seu objetivo
                    </Box>
                  </>
                ) : (
                  <>Alunos procurando personal perto de você.</>
                )}
              </Typography>
              <Typography
                variant="h6"
                component="p"
                color="text.secondary"
                sx={{ fontWeight: 400, maxWidth: 580 }}
              >
                {who === 'student'
                  ? 'Perto de casa, na academia, ao ar livre ou online. Compare especialidades e preços e peça contato de graça.'
                  : 'Crie seu perfil grátis, apareça no Google e receba pedidos de quem quer começar a treinar.'}
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={who}
                onChange={(_, value) => value && setWho(value)}
                aria-label="Você está procurando"
                sx={(theme) => ({
                  alignSelf: 'flex-start',
                  p: 0.5,
                  gap: 0.5,
                  borderRadius: 99,
                  bgcolor: theme.vars.palette.background.paper,
                  border: `1px solid ${theme.vars.palette.divider}`,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '99px !important',
                    px: 2.25,
                    gap: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    transition: transition(['background-color', 'color'], 'medium1'),
                  },
                  '& .Mui-selected': {
                    bgcolor: `${theme.vars.palette.primary.main} !important`,
                    color: `${theme.vars.palette.primary.contrastText} !important`,
                  },
                })}
              >
                <ToggleButton value="student">
                  <PersonSearchRounded fontSize="small" />
                  Quero um personal
                </ToggleButton>
                <ToggleButton value="trainer">
                  <SportsGymnasticsRounded fontSize="small" />
                  Sou personal, busco alunos
                </ToggleButton>
              </ToggleButtonGroup>
              {who === 'student' ? (
                <>
                  <SearchPanel />
                  <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {QUICK_SEARCHES.map((item) => (
                      <Chip
                        key={item.label}
                        label={item.label}
                        component={RouterLink}
                        to={item.to}
                        clickable
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </>
              ) : (
                <TrainerTeaser />
              )}
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ position: 'relative', minHeight: 520 }}>
              {featured.isPending &&
                [0, 1].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={300}
                    sx={{ position: 'absolute', width: 320, top: i * 150, left: i * 90 }}
                  />
                ))}
              {cards.slice(0, 2).map((card, i) => (
                <Box
                  key={card.slug}
                  sx={{
                    position: 'absolute',
                    width: 320,
                    top: i * 170,
                    left: i ? 110 : 0,
                    transform: `rotate(${i ? 3 : -3}deg)`,
                    zIndex: 2 - i,
                    animation: `float-${i} ${7 + i * 2}s ease-in-out infinite`,
                    [`@keyframes float-${i}`]: {
                      '0%, 100%': { translate: '0 0' },
                      '50%': { translate: `0 ${i ? 10 : -10}px` },
                    },
                  }}
                >
                  <ProfileCardView card={card} />
                </Box>
              ))}
              {cards.length > 0 && (
                <Paper
                  aria-hidden
                  elevation={0}
                  sx={(theme) => ({
                    position: 'absolute',
                    zIndex: 3,
                    left: -36,
                    top: 400,
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'center',
                    border: `1px solid ${theme.vars.palette.divider}`,
                    boxShadow: `0 20px 50px -20px ${theme.alpha('#000', 0.35)}`,
                    animation: 'pop-in 900ms 600ms both cubic-bezier(0.34, 1.56, 0.64, 1)',
                    '@keyframes pop-in': { from: { opacity: 0, transform: 'scale(0.6)' } },
                  })}
                >
                  <CheckCircleRounded color="success" />
                  <Box>
                    <Typography variant="subtitle2">Pedido aceito!</Typography>
                    <Typography variant="caption" color="text.secondary">
                      O WhatsApp do personal foi liberado.
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function TrainerTeaser() {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: { xs: 2.5, sm: 3 },
        borderRadius: `${Number(theme.shape.borderRadius) * 2.2}px`,
        borderStyle: 'dashed',
        borderWidth: 2,
      })}
    >
      <Stack spacing={2}>
        <Chip
          size="small"
          variant="soft"
          color="warning"
          label="Busca de alunos: em breve"
          sx={{ alignSelf: 'flex-start' }}
        />
        <Typography>
          Logo você vai poder encontrar alunos da sua região que estão procurando personal. Enquanto
          isso, crie seu perfil grátis: os alunos já podem te encontrar e pedir contato.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            component={RouterLink}
            to="/painel"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardRounded />}
          >
            Criar meu perfil grátis
          </Button>
          <Button component="a" href="#para-personais" size="large">
            Ver o que você ganha
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function TrustStrip() {
  const items = [
    {
      icon: <VerifiedRounded />,
      title: 'CREF conferido',
      text: 'O selo mostra quem já teve o registro no conselho verificado pela equipe.',
    },
    {
      icon: <LockRounded />,
      title: 'Seu contato protegido',
      text: 'Telefone e e-mail só vão para o personal que você escolheu, depois que ele aceitar.',
    },
    {
      icon: <HandshakeRounded />,
      title: 'Direto com o personal',
      text: 'Valores, horários e pagamento das aulas vocês combinam direto, sem intermediário.',
    },
  ];
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: -3, md: -5 }, position: 'relative', zIndex: 1 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4 }}>
        <Grid container spacing={3}>
          {items.map((item) => (
            <Grid key={item.title} size={{ xs: 12, md: 4 }}>
              <Stack direction="row" spacing={1.75}>
                <Box
                  aria-hidden
                  sx={(theme) => ({
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 2.5,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: theme.vars.palette.primary.container,
                    color: theme.vars.palette.primary.onContainer,
                  })}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.text}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Container>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: <PersonSearchRounded />,
      title: 'Busque',
      text: 'Filtre por cidade, bairro, objetivo, preço e atendimento online.',
    },
    {
      icon: <ForumRounded />,
      title: 'Peça contato',
      text: 'Conte seu objetivo em poucas linhas. É grátis e leva um minuto.',
    },
    {
      icon: <CheckCircleRounded />,
      title: 'Combine direto',
      text: 'Quando o personal aceitar, vocês recebem o contato um do outro.',
    },
  ];
  return (
    <Container
      component="section"
      maxWidth="lg"
      sx={{ py: { xs: 8, md: 10 } }}
      aria-labelledby="como"
    >
      <Typography id="como" variant="h3" component="h2" sx={{ textAlign: 'center', mb: 5 }}>
        Como funciona
      </Typography>
      <Grid container spacing={3}>
        {steps.map((step, i) => (
          <Grid key={step.title} size={{ xs: 12, md: 4 }}>
            <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center', px: 2 }}>
              <Box
                sx={(theme) => ({
                  position: 'relative',
                  width: 72,
                  height: 72,
                  borderRadius: '28%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: theme.vars.palette.primary.main,
                  color: theme.vars.palette.primary.contrastText,
                  '& svg': { fontSize: 34 },
                  boxShadow: `0 16px 30px -16px ${theme.vars.palette.primary.main}`,
                })}
              >
                {step.icon}
                <Box
                  sx={(theme) => ({
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 800,
                    fontSize: 13,
                    bgcolor: theme.vars.palette.accent.main,
                    color: theme.vars.palette.accent.contrastText,
                  })}
                >
                  {i + 1}
                </Box>
              </Box>
              <Typography variant="h5" component="h3">
                {step.title}
              </Typography>
              <Typography color="text.secondary">{step.text}</Typography>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

function Featured() {
  const result = useProfessionalSearch({ pageSize: 6 });
  const items = result.data?.items ?? [];
  if (!result.isPending && items.length === 0) return null;
  return (
    <Container
      component="section"
      maxWidth="lg"
      sx={{ pb: { xs: 6, md: 8 } }}
      aria-labelledby="destaques"
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, gap: 2 }}
      >
        <Box>
          <Typography id="destaques" variant="h4" component="h2">
            Personais em destaque
          </Typography>
          <Typography color="text.secondary">
            Perfis completos e com CREF verificado aparecem primeiro.
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/buscar"
          endIcon={<ArrowForwardRounded />}
          sx={{ flexShrink: 0 }}
        >
          Ver todos
        </Button>
      </Stack>
      <Grid container spacing={3}>
        {result.isPending
          ? [0, 1, 2].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Skeleton variant="rounded" height={320} />
              </Grid>
            ))
          : items.map((card) => (
              <Grid key={card.slug} size={{ xs: 12, sm: 6, md: 4 }}>
                <ProfileCardView card={card} />
              </Grid>
            ))}
      </Grid>
    </Container>
  );
}

function PopularCities() {
  const cities = useCitySuggestions('');
  const list = cities.data ?? [];
  if (list.length === 0) return null;
  return (
    <Container
      component="section"
      maxWidth="lg"
      sx={{ pb: { xs: 6, md: 8 } }}
      aria-labelledby="cidades"
    >
      <Typography id="cidades" variant="h5" component="h2" sx={{ mb: 2 }}>
        Personal trainer por cidade
      </Typography>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
        {list.map((c) => (
          <Chip
            key={`${c.city}-${c.uf}`}
            component={RouterLink}
            to={cityPath(c.uf, c.city)}
            clickable
            variant="soft"
            color="primary"
            label={`${c.city}, ${c.uf} · ${c.count}`}
          />
        ))}
      </Stack>
    </Container>
  );
}
